import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Search } from 'lucide-react'
import { Avatar } from '../../../../components/ui/Avatar'
import { useAgentSearch } from '../../hooks/useAgentSearch'
import { useUser } from '../../../../contexts/UserContext'

interface AgentSearchViewProps {
    onSelectAgent: (agentId: string | null, agentName: string) => void
    isFullscreen?: boolean
}

export const AgentSearchView: React.FC<AgentSearchViewProps> = ({
    onSelectAgent,
    isFullscreen = false
}) => {
    const { user } = useUser()
    const { agents } = useAgentSearch(user?.id || null, true)
    const [searchQuery, setSearchQuery] = useState('')

    // Reset scroll on mount
    useEffect(() => {
        setSearchQuery('')
    }, [])

    // Filter agents
    const filteredAgents = agents.filter(agent =>
        agent.name.toLowerCase().includes(searchQuery.toLowerCase())
    )

    return (
        <motion.div
            {...({
                initial: { opacity: 0, x: 20 },
                animate: { opacity: 1, x: 0 },
                exit: { opacity: 0, x: 20 }
            } as any)}
            className={`flex flex-col h-full w-full bg-white dark:bg-black ${isFullscreen ? 'max-w-4xl mx-auto' : ''}`}
        >
            {/* Title - Optional, matching Settings style if needed, or just Search Input */}
            <div className="px-4 py-2">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Selecionar Agente
                </h2>
            </div>

            {/* Search Input */}
            <div className="p-4">
                <div className="relative group">
                    <div className="rounded-full p-[1px] bg-gray-200 dark:bg-gray-800 group-focus-within:dark:bg-gradient-to-br group-focus-within:dark:from-blue-400/40 group-focus-within:dark:via-purple-500/40 group-focus-within:dark:to-pink-400/30 transition-all duration-200">
                        <div className="relative bg-white dark:bg-gray-950 rounded-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 z-10" />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Buscar agentes..."
                                className="w-full pl-10 pr-4 h-10 bg-transparent border-0 rounded-full dark:text-white dark:placeholder-gray-500 focus:ring-0 text-sm focus:outline-none"
                                autoFocus
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Agents List */}
            <div className="flex-1 overflow-y-auto">
                <div className="px-2">
                    {filteredAgents.map(agent => (
                        <div
                            key={agent.id}
                            onClick={() => onSelectAgent(agent.id, agent.name)}
                            className="p-3 mb-1 hover:bg-gray-50 dark:hover:bg-gray-900/50 rounded-lg cursor-pointer transition-colors group"
                        >
                            <div className="flex items-center gap-4">
                                <Avatar size="md" alt={agent.name} src={agent.avatar_url || undefined} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                        {agent.name}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {agent.description || 'Agente personalizado'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}

                    {filteredAgents.length === 0 && searchQuery && (
                        <div className="p-8 text-center">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Nenhum agente encontrado para "{searchQuery}"
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    )
}
